// src/components/SidebarWaitingList.tsx
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  List, Card, Tag, Typography, Spin, Empty, Badge, Button, DatePicker,
  Modal, Input, Select, Space, message, Divider, Avatar, Descriptions, Collapse,
  Alert, Row, Col, Checkbox,
} from 'antd';
import {
  UserOutlined, ReloadOutlined, CheckCircleOutlined, PlusOutlined,
  SearchOutlined, UserAddOutlined, PhoneOutlined, EyeOutlined,
  FileTextOutlined, MedicineBoxOutlined, PictureOutlined, GiftOutlined,
} from '@ant-design/icons';
import {
  getFirestore,
  collection,
  query,
  getDocs,
  onSnapshot,
  where,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { cacheOwner, cachedPatients } from '../offline/profileCache';
import { canCaptureDoctor } from '../offline/config';
import { queueWaitingPatient, pendingWaiting, markWaitingPatientEntered } from '../offline/waitingQueue';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { usePatientContext } from './PatientContext';
import API from '../config/api';
import {
  getDisplayedQueuePosition,
  getNextDoctorQueuePosition,
} from '../utils/queuePosition';
import {
  NewPatientEntryErrors,
  PatientAgeUnit,
  normalizePatientPhoneInput,
  phoneDigits,
  validateNewPatientEntry,
} from '../utils/patientEntryValidation';
import { assistantWaitingListDateKey, waitingListDateKeys } from '../utils/waitingListDateKeys';

const { Text } = Typography;
const { Option } = Select;

// ── Billing receipt helpers (kept for reference — printing moved to PatientDetail) ──

function formatReceiptDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('ar-EG', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return dateStr; }
}

function printBillingReceipt(patient: any, settings: any, latestDiagnosis?: string): void {
  const ps = settings?.printSettings ?? {
    paperSize: 'a4', marginTop: 0, marginLeft: 0, marginRight: 0,
    showHeader: true, showFooter: true,
  };
  const showHeader = ps.showHeader !== false;

  const lineItems: Array<{ description: string; category: string; amount: number; status: string; method: string }> = [];

  const consult = patient.consultationPayment;
  if (consult && Number(consult.amount) > 0) {
    lineItems.push({
      description: consult.consultationType || patient.visit_type || 'كشف',
      category: 'زيارة',
      amount: Number(consult.amount),
      status: consult.paymentStatus,
      method: consult.paymentMethod,
    });
  }

  for (const bill of (patient.bills ?? [])) {
    // Always include consultation fee if present
    if (Number(bill.consultationFee) > 0) {
      lineItems.push({
        description: bill.consultationType || patient.visit_type || 'كشف',
        category: 'زيارة',
        amount: Number(bill.consultationFee),
        status: bill.paymentStatus,
        method: bill.paymentMethod,
      });
    }
    // Include all services
    for (const svc of (bill.services ?? [])) {
      lineItems.push({
        description: svc.service_name ?? svc.name ?? 'خدمة',
        category: 'خدمة',
        amount: Number(svc.subtotal ?? svc.price ?? 0),
        status: bill.paymentStatus,
        method: bill.paymentMethod,
      });
    }
  }

  const totalAmount   = lineItems.reduce((s, i) => s + i.amount, 0);
  const paidAmount    = lineItems.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const pendingAmount = totalAmount - paidAmount;

  const statusLabel = (status: string, method: string) => {
    if (status === 'paid') return `مدفوع (${method === 'cash' ? 'نقداً' : 'كارت'})`;
    if (status === 'refund_due') return 'مستحق استرداد';
    return 'غير مدفوع';
  };
  const statusColor = (status: string) => {
    if (status === 'paid') return '#16a34a';
    if (status === 'refund_due') return '#d97706';
    return '#dc2626';
  };

  const receiptId = `${(patient.date ?? '').replace(/-/g, '')}-${(patient.file_number ?? patient.id ?? 'xxxx').toString().slice(-4)}`;

  const headerParts: string[] = [];
  if (settings?.clinicName)    headerParts.push(`<div class="clinic-name">${settings.clinicName}</div>`);
  if (settings?.doctorTitle)   headerParts.push(`<div class="doctor-title">${settings.doctorTitle}</div>`);
  if (settings?.clinicAddress) headerParts.push(`<div class="clinic-detail">📍 ${settings.clinicAddress}</div>`);
  if (settings?.clinicPhone)   headerParts.push(`<div class="clinic-detail">📞 ${settings.clinicPhone}</div>`);

  const linesHtml = lineItems.map((item, idx) => `
    <tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">
      <td class="td-num">${idx + 1}</td>
      <td class="td-desc">
        <span class="item-cat">${item.category}</span>
        ${item.description}
      </td>
      <td class="td-amount">${item.amount.toLocaleString('ar-EG')} ج</td>
      <td class="td-status" style="color:${statusColor(item.status)}">
        ${statusLabel(item.status, item.method)}
      </td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8"/>
  <title>إيصال — ${patient.patient_name ?? ''}</title>
  <style>
    @page {
      size: ${ps.paperSize === 'custom' ? 'auto' : (ps.paperSize || 'a4')};
      margin: 0;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Arial, 'Segoe UI', sans-serif;
      direction: rtl;
      color: #111;
      font-size: 13px;
      background: #fff;
    }
    .page {
      padding-top: ${((ps.marginTop || 0) / 2)}mm;
      padding-right: calc(${((ps.marginRight || 0) / 2)}mm + 14px);
      padding-left:  calc(${((ps.marginLeft  || 0) / 2)}mm + 14px);
      padding-bottom: 20px;
    }
    .header {
      display: ${showHeader ? 'block' : 'none'};
      text-align: center;
      border-bottom: 3px double #1a56db;
      padding-bottom: 12px;
      margin-bottom: 14px;
    }
    .clinic-name  { font-size: 20px; font-weight: 800; color: #1a56db; }
    .doctor-title { font-size: 14px; font-weight: 600; color: #374151; margin-top: 4px; }
    .clinic-detail{ font-size: 12px; color: #6b7280; margin-top: 3px; }
    .receipt-title-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 14px;
      gap: 12px;
    }
    .receipt-title {
      font-size: 17px; font-weight: 800; color: #111;
      border-right: 4px solid #1a56db;
      padding-right: 8px;
    }
    .receipt-meta { text-align: left; font-size: 11px; color: #6b7280; line-height: 1.7; }
    .receipt-meta strong { color: #111; }
    .patient-box {
      background: #f0f7ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      padding: 10px 14px;
      margin-bottom: 16px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px 20px;
    }
    .patient-field { font-size: 12px; color: #374151; }
    .patient-field span { font-weight: 700; color: #111; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    thead th {
      background: #1a56db;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      padding: 8px 10px;
      text-align: right;
    }
    td { padding: 7px 10px; font-size: 12px; vertical-align: middle; }
    .row-even { background: #fff; }
    .row-odd  { background: #f9fafb; }
    tr:not(:last-child) td { border-bottom: 1px solid #e5e7eb; }
    .td-num    { width: 28px; color: #9ca3af; text-align: center; }
    .td-amount { width: 90px; text-align: center; font-weight: 700; white-space: nowrap; }
    .td-status { width: 130px; font-size: 11px; font-weight: 600; white-space: nowrap; }
    .item-cat  {
      display: inline-block;
      font-size: 10px; font-weight: 700;
      background: #e0e7ff; color: #3730a3;
      border-radius: 4px; padding: 1px 5px;
      margin-left: 6px;
    }
    .totals {
      width: 260px;
      margin-right: auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 20px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 7px 14px;
      font-size: 13px;
    }
    .total-row + .total-row { border-top: 1px solid #e5e7eb; }
    .total-row.grand  { background: #1a56db; color: #fff; font-weight: 800; font-size: 14px; }
    .total-row.paid-row    { color: #16a34a; }
    .total-row.pending-row { color: #dc2626; }
    .footer-section {
      display: ${(showHeader && settings?.receiptFooter) ? 'block' : 'none'};
      text-align: center;
      border-top: 1px dashed #d1d5db;
      padding-top: 10px;
      font-size: 11px;
      color: #6b7280;
      margin-bottom: 16px;
    }
    .sig-row {
      display: flex;
      justify-content: space-between;
      margin-top: 24px;
      gap: 20px;
    }
    .sig-box {
      flex: 1;
      border-top: 1px solid #9ca3af;
      padding-top: 6px;
      text-align: center;
      font-size: 11px;
      color: #6b7280;
    }
    .last-diagnosis-box {
      margin-bottom: 14px;
      padding: 10px 12px;
      background: #f6ffed;
      border: 1px solid #b7eb8f;
      border-radius: 8px;
      font-size: 12px;
      color: #333;
    }
    .last-diagnosis-text { margin-top: 6px; line-height: 1.5; white-space: pre-wrap; }
    @media print { .no-print { display: none !important; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    ${headerParts.length > 0 ? headerParts.join('\n') : '<div class="clinic-name">إيصال طبي</div>'}
    ${settings?.receiptHeader ? `<div style="margin-top:6px;font-size:12px;color:#374151">${settings.receiptHeader}</div>` : ''}
  </div>
  <div class="receipt-title-row">
    <div class="receipt-title">إيصال خدمات طبية</div>
    <div class="receipt-meta">
      <strong>رقم الإيصال:</strong> ${receiptId}<br/>
      <strong>التاريخ:</strong> ${formatReceiptDate(patient.date)}<br/>
      <strong>الوقت:</strong> ${patient.time ?? ''}
    </div>
  </div>
  <div class="patient-box">
    <div class="patient-field">الاسم: <span>${patient.patient_name ?? ''}</span></div>
    <div class="patient-field">رقم الملف: <span>${patient.file_number ?? '—'}</span></div>
    <div class="patient-field">الهاتف: <span>${patient.patient_phone ?? ''}</span></div>
    <div class="patient-field">نوع الزيارة: <span>${patient.visit_type ?? ''}${patient.visit_speed === 'سريع' ? ' ⚡' : ''}</span></div>
    ${patient.doctor_name ? `<div class="patient-field">الطبيب: <span>د. ${patient.doctor_name}</span></div>` : ''}
  </div>
  ${latestDiagnosis ? `<div class="last-diagnosis-box"><strong>Latest diagnosis / آخر تشخيص:</strong><div class="last-diagnosis-text">${latestDiagnosis.replace(/\n/g, '<br/>')}</div></div>` : ''}
  <table>
    <thead>
      <tr>
        <th class="td-num">#</th>
        <th>البيان</th>
        <th class="td-amount">المبلغ</th>
        <th class="td-status">حالة الدفع</th>
      </tr>
    </thead>
    <tbody>
      ${lineItems.length > 0 ? linesHtml : '<tr><td colspan="4" style="text-align:center;color:#9ca3af;padding:16px">لا توجد خدمات مسجلة</td></tr>'}
    </tbody>
  </table>
  <div class="totals">
    <div class="total-row grand">
      <span>الإجمالي الكلي</span>
      <span>${totalAmount.toLocaleString('ar-EG')} ج</span>
    </div>
    ${paidAmount > 0 ? `<div class="total-row paid-row"><span>✅ المدفوع</span><span>${paidAmount.toLocaleString('ar-EG')} ج</span></div>` : ''}
    ${pendingAmount > 0 ? `<div class="total-row pending-row"><span>⏳ المتبقي</span><span>${pendingAmount.toLocaleString('ar-EG')} ج</span></div>` : ''}
  </div>
  <div class="footer-section">${settings?.receiptFooter ?? ''}</div>
  <div class="sig-row">
    <div class="sig-box">توقيع المريض</div>
    <div class="sig-box">ختم العيادة</div>
  </div>
</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

interface WaitingPatient {
  id: string; // Firestore doc id
  patient_id: string;
  patient_name: string;
  patient_phone?: string;
  arrivalTime?: any;
  status?: string;
  position?: number;
  doctor_id?: string;
  visit_type?: string;
  nationality?: string;
  age?: string;
  address?: string;
  date?: string;
  time?: string;
  offlinePending?: boolean;
  // Firestore rows can be merged from several clinics. Keep the source path
  // with the row so Enter updates the exact document that was rendered.
  waiting_clinic_id?: string;
  waiting_date_key?: string;
  clinic_id?: string;
}

interface PatientResult {
  _id: string;
  patient_id?: string;
  patient_name: string;
  patient_phone?: string;
  age?: string;
  file_number?: string;
  file_id?: string;
  payment_type?: string;
  insurance_company_id?: string;
  insurance_company_name?: string;
  insurance_number?: string;
  coverage_percentage?: number;
}

interface SidebarWaitingListProps {
  refreshTrigger?: number;
}

const VISIT_TYPES = [
  { label: 'كشف',           value: 'كشف' },
  { label: 'إعادة كشف',    value: 'إعادة كشف' },
  { label: 'استشارة',      value: 'استشارة' },
  { label: 'أخرى',         value: 'أخرى' },
];

const SidebarWaitingList = forwardRef<{ refreshData: () => Promise<void> }, SidebarWaitingListProps>(
  ({ refreshTrigger = 0 }, ref) => {
    const [waitingPatients, setWaitingPatients] = useState<WaitingPatient[]>([]);
    const [localWaiting, setLocalWaiting] = useState<WaitingPatient[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const { selectedClinicId, clinicScopeIds } = useClinicContext();
    const { doctorName, settings: doctorSettings } = useDoctorContext();
    // Refresh the patient list directly when Enter-to-doctor succeeds, so the
    // newly entered patient appears immediately. We don't rely on the
    // Firestore listener path (it's debounced 1s and skipped while a
    // PatientDetail is open) for this user-initiated action.
    const { fetchPatients, clearPatientListCache, upsertPatientLocally, setSelectedPatient: openPatientProfile } = usePatientContext();
    const doctorId = localStorage.getItem('doctorId');
    const [checkInLoading, setCheckInLoading] = useState(false);

    // ── Add Patient Modal state ───────────────────────────────────────────────
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<PatientResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<PatientResult | null>(null);
    const [visitType, setVisitType] = useState('كشف');
    const [addingPatient, setAddingPatient] = useState(false);
    // New (unregistered) patient fields
    const [newPatientMode, setNewPatientMode] = useState(false);
    const [newPatientName, setNewPatientName] = useState('');
    const [newPatientPhone, setNewPatientPhone] = useState('');
    const [newPatientAge, setNewPatientAge] = useState('');
    const [newPatientAgeUnit, setNewPatientAgeUnit] = useState<PatientAgeUnit>('سنة');
    const [newPatientNationality, setNewPatientNationality] = useState('');
    const [newPatientAddress, setNewPatientAddress] = useState('');
    const [newPatientErrors, setNewPatientErrors] = useState<NewPatientEntryErrors>({});
    const [phoneMatches, setPhoneMatches] = useState<PatientResult[]>([]);
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [confirmedSharedPhone, setConfirmedSharedPhone] = useState(false);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const waitingOperationRef = useRef<string>('');
    useEffect(() => {
      let active = true;
      const refresh = async () => {
        const rows = await pendingWaiting();
        if (active) setLocalWaiting(rows.filter((row: any) => row.kind !== 'reminder' && !row.entered &&
          (selectedClinicId === '__all_clinics__' ? clinicScopeIds?.includes(row.clinicId) : row.clinicId === selectedClinicId) &&
          row.date === toFirestoreDateStr(selectedDate))
          .map((row: any) => ({ ...row.patient, id: row.existingWaitingDocId || row.operationId,
            waiting_clinic_id: row.clinicId, clinic_id: row.patient.clinic_id || row.clinicId, offlinePending: true })));
      };
      void refresh().catch(() => {});
      window.addEventListener('offlineWaitingChanged', refresh);
      window.addEventListener('doctorIdChanged', refresh);
      return () => { active = false; window.removeEventListener('offlineWaitingChanged', refresh); window.removeEventListener('doctorIdChanged', refresh); };
    }, [selectedClinicId, clinicScopeIds, selectedDate, doctorId]);

    // ── Check-in Confirm state ────────────────────────────────────────────────
    const [checkInTarget, setCheckInTarget] = useState<WaitingPatient | null>(null);

    // ── Quick-view patient history state ──────────────────────────────────────
    const [quickViewPatient, setQuickViewPatient] = useState<WaitingPatient | null>(null);
    const [quickViewLoading, setQuickViewLoading] = useState(false);
    const [quickViewVisits, setQuickViewVisits] = useState<any[]>([]);
    const [quickViewMedHistory, setQuickViewMedHistory] = useState<any>(null);
    const [quickViewReports, setQuickViewReports] = useState<any[]>([]);
    const [quickViewPackages, setQuickViewPackages] = useState<any[]>([]);
    const [quickViewActivePackages, setQuickViewActivePackages] = useState<any[]>([]);

    // Expose refresh method to parent via ref
    useImperativeHandle(ref, () => ({
      refreshData: fetchData
    }));

    const toFirestoreDateStr = (d: Dayjs): string => d.format('YYYY-MM-DD');

    const getClinicIds = (): string[] => {
      if (!selectedClinicId) return [];
      if (selectedClinicId === '__all_clinics__') {
        return clinicScopeIds && clinicScopeIds.length > 0 ? clinicScopeIds : [];
      }
      return [selectedClinicId];
    };

    const fetchData = async (dateOverride?: Dayjs) => {
      if (!selectedClinicId) {
        console.warn('[WaitingList] ⚠️ No selectedClinicId — skipping fetch');
        return;
      }
      const clinicIds = getClinicIds();
      const requestedDate = dateOverride || selectedDate;
      const currentDate = toFirestoreDateStr(requestedDate);
      const dateKeys = waitingListDateKeys(requestedDate);

      console.group('[WaitingList] 🔍 fetchData called');
      console.log('  selectedClinicId  :', selectedClinicId);
      console.log('  clinicIds         :', clinicIds);
      console.log('  doctorId (filter) :', doctorId ?? '(none — no doctor filter applied)');
      console.log('  date queried      :', currentDate);
      console.log('  localStorage.clinicId :', localStorage.getItem('clinicId'));
      console.log('  localStorage.doctorId :', localStorage.getItem('doctorId'));
      console.groupEnd();

      if (clinicIds.length === 0) {
        console.warn('[WaitingList] ⚠️ clinicIds is empty — no query will run');
        setWaitingPatients([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const db = getFirestore();

      try {
        const allPatients: WaitingPatient[] = [];

        await Promise.all(
          clinicIds.flatMap((clinicId) => dateKeys.map(async (dateKey) => {
            const path = `clinics/${clinicId}/waiting_list/${dateKey}/patients`;
            console.log(`[WaitingList] 📂 Querying path: ${path}`);
            console.log(`[WaitingList]    + where status == "WAITING"`);
            if (doctorId) console.log(`[WaitingList]    + where doctor_id == "${doctorId}"`);

            const waitingListRef = collection(db, 'clinics', clinicId, 'waiting_list', dateKey, 'patients');
            const constraints: any[] = [where('status', '==', 'WAITING')];
            if (doctorId) constraints.push(where('doctor_id', '==', doctorId));
            const waitingQuery = query(waitingListRef, ...constraints);
            const querySnapshot = await getDocs(waitingQuery);

            console.log(`[WaitingList] ✅ Path "${path}" returned ${querySnapshot.size} WAITING patient(s)`);
            if (querySnapshot.size === 0 && process.env.NODE_ENV === 'development') {
              console.debug(`[WaitingList] 0 waiting patients at ${path}`);
            }

            querySnapshot.forEach((docSnap: any) => {
              const data = docSnap.data();
              console.log(`[WaitingList]    patient: "${data.patient_name}" | status: ${data.status} | doctor_id: ${data.doctor_id} | doc_id: ${docSnap.id}`);
              allPatients.push({
                id: docSnap.id,
                waiting_clinic_id: clinicId,
                waiting_date_key: dateKey,
                clinic_id: data.clinic_id || clinicId,
                patient_id: data.patient_id || docSnap.id,
                patient_name: data.patient_name || 'Unknown Patient',
                patient_phone: data.patient_phone,
                age: data.age,
                address: data.address,
                nationality: data.nationality,
                arrivalTime: data.arrivalTime || data.time,
                status: data.status || 'WAITING',
                position: data.position || data.user_order_in_queue || 0,
                doctor_id: data.doctor_id || doctorId || '',
                visit_type: data.visit_type || 'كشف',
                date: data.date,
                time: data.time,
                offlinePending: docSnap.metadata?.hasPendingWrites || data.offlinePending === true,
              });
            });
          }))
        );

        const uniquePatients = Array.from(new Map<string, WaitingPatient>(
          allPatients.map(patient => [`${patient.waiting_clinic_id}|${patient.id}`, patient])
        ).values());
        console.log(`[WaitingList] 🏁 Total WAITING patients found across all clinics: ${uniquePatients.length}`);

        uniquePatients.sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) return a.position - b.position;
          const timeA = a.arrivalTime
            ? (typeof a.arrivalTime.toDate === 'function' ? a.arrivalTime.toDate().getTime() : new Date(a.arrivalTime).getTime())
            : 0;
          const timeB = b.arrivalTime
            ? (typeof b.arrivalTime.toDate === 'function' ? b.arrivalTime.toDate().getTime() : new Date(b.arrivalTime).getTime())
            : 0;
          return timeA - timeB;
        });

        setWaitingPatients(uniquePatients);
      } catch (error: any) {
        console.error('[WaitingList] ❌ Error fetching waiting list:', error);
        setWaitingPatients([]);
      } finally {
        setLoading(false);
      }
    };

    const formatTime = (timestamp: any) => {
      if (!timestamp) return '';
      try {
        if (typeof timestamp === 'string') {
          return timestamp.includes(' ') ? dayjs(timestamp).format('HH:mm') : timestamp;
        }
        if (typeof timestamp === 'object' && timestamp.toDate) {
          return dayjs(timestamp.toDate()).format('HH:mm');
        }
        return dayjs(timestamp).format('HH:mm');
      } catch {
        return '';
      }
    };

    // Real-time listener — supports single clinic or "all clinics" (fan-out)
    useEffect(() => {
      if (!selectedClinicId) {
        console.warn('[WaitingList] listener skipped: no selectedClinicId (sidebar waiting list inactive)');
        return;
      }
      const clinicIds = getClinicIds();
      if (clinicIds.length === 0) {
        console.warn('[WaitingList] listener skipped: clinicIds empty (e.g. "all clinics" with no scope)', {
          selectedClinicId,
          clinicScopeIds,
        });
        setWaitingPatients([]);
        return;
      }

      const db = getFirestore();
      const currentDate = toFirestoreDateStr(selectedDate);
      const dateKeys = waitingListDateKeys(selectedDate);
      console.log('[WaitingList] onSnapshot subscribing', {
        clinicIds,
        dateSegment: currentDate,
        doctorFilter: doctorId ?? '(none)',
      });

      // Holds the latest snapshot results per clinic so merges are consistent
      const snapshotsByClinic = new Map<string, WaitingPatient[]>();

      const mergeAndSet = () => {
        const merged: WaitingPatient[] = [];
        snapshotsByClinic.forEach((list) => merged.push(...list));
        const unique = Array.from(new Map<string, WaitingPatient>(
          merged.map(patient => [`${patient.waiting_clinic_id}|${patient.id}`, patient])
        ).values());
        unique.sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) return a.position - b.position;
          const tA = a.arrivalTime
            ? (typeof a.arrivalTime.toDate === 'function' ? a.arrivalTime.toDate().getTime() : new Date(a.arrivalTime).getTime())
            : 0;
          const tB = b.arrivalTime
            ? (typeof b.arrivalTime.toDate === 'function' ? b.arrivalTime.toDate().getTime() : new Date(b.arrivalTime).getTime())
            : 0;
          return tA - tB;
        });
        setWaitingPatients(unique);
        setLoading(false);
      };

      const unsubscribers = clinicIds.flatMap((clinicId) => dateKeys.map((dateKey) => {
        const waitingListRef = collection(db, 'clinics', clinicId, 'waiting_list', dateKey, 'patients');
        const constraints: any[] = [where('status', '==', 'WAITING')];
        if (doctorId) constraints.push(where('doctor_id', '==', doctorId));
        const waitingQuery = query(waitingListRef, ...constraints);

        return onSnapshot(
          waitingQuery,
          (snapshot: any) => {
            console.log('[WaitingList] onSnapshot update', {
              path: `clinics/${clinicId}/waiting_list/${dateKey}/patients`,
              docs: snapshot.docs.length,
              pendingWrites: snapshot.metadata.hasPendingWrites,
            });
            const patients: WaitingPatient[] = snapshot.docs.map((docSnap: any) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                waiting_clinic_id: clinicId,
                waiting_date_key: dateKey,
                clinic_id: data.clinic_id || clinicId,
                patient_id: data.patient_id || docSnap.id,
                patient_name: data.patient_name || 'Unknown Patient',
                patient_phone: data.patient_phone,
                age: data.age,
                address: data.address,
                nationality: data.nationality,
                arrivalTime: data.arrivalTime || data.time,
                status: data.status || 'WAITING',
                position: data.position || data.user_order_in_queue || 0,
                doctor_id: data.doctor_id || doctorId || '',
                visit_type: data.visit_type || 'كشف',
                date: data.date,
                time: data.time,
                offlinePending: docSnap.metadata?.hasPendingWrites || data.offlinePending === true,
              };
            });
            snapshotsByClinic.set(`${clinicId}|${dateKey}`, patients);
            mergeAndSet();
          },
          () => fetchData(selectedDate)
        );
      }));

      fetchData(selectedDate);
      return () => unsubscribers.forEach((unsub) => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClinicId, clinicScopeIds, doctorId, selectedDate]);

    // Builds released before this compatibility fix wrote offline queue rows to
    // YYYY-MM-DD, while the assistant portal reads YYYY-M-D. Move only our
    // operation-tagged rows to the assistant path; normal queue rows are left
    // untouched. The operation id makes this migration safe and idempotent.
    useEffect(() => {
      if (!navigator.onLine || !selectedClinicId || !doctorId) return;
      const paddedDate = toFirestoreDateStr(selectedDate);
      const assistantDate = assistantWaitingListDateKey(selectedDate);
      if (paddedDate === assistantDate) return;
      let cancelled = false;
      const migrate = async () => {
        const db = getFirestore();
        for (const clinicId of getClinicIds()) {
          if (cancelled) return;
          const sourceList = collection(db, 'clinics', clinicId, 'waiting_list', paddedDate, 'patients');
          const snapshot = await getDocs(query(sourceList, where('doctor_id', '==', doctorId)));
          for (const sourceDoc of snapshot.docs) {
            if (cancelled || !sourceDoc.data().offline_operation_id) continue;
            const source = doc(sourceList, sourceDoc.id);
            const target = doc(db, 'clinics', clinicId, 'waiting_list', assistantDate, 'patients', sourceDoc.id);
            await runTransaction(db, async transaction => {
              const [freshSource, existingTarget] = await Promise.all([
                transaction.get(source), transaction.get(target),
              ]);
              if (!freshSource.exists()) return;
              const sourceData = freshSource.data();
              if (!sourceData.offline_operation_id) return;
              if (!existingTarget.exists()) {
                transaction.set(target, { ...sourceData, date: assistantDate });
                transaction.delete(source);
              } else if (existingTarget.data().offline_operation_id === sourceData.offline_operation_id) {
                transaction.delete(source);
              }
            });
          }
        }
      };
      void migrate().catch(error => console.warn('[WaitingList] Could not reconcile assistant date path:', error));
      return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClinicId, clinicScopeIds, doctorId, selectedDate]);

    useEffect(() => {
      if (refreshTrigger > 0 && selectedClinicId) fetchData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    const handleDateChange = (date: Dayjs | null) => {
      if (date) setSelectedDate(date);
    };

    const getStatusTag = (status: string) => {
      const v = (status || '').trim();
      const isEstshara = v.toLowerCase() === 'estshara' || v.includes('استشارة') || v.includes('استشاره');
      switch (true) {
        case isEstshara:        return <Tag color="purple">Consultation</Tag>;
        case v === 'كشف':       return <Tag color="blue">كشف</Tag>;
        case v === 'إعادة كشف' || v.includes('إعادة') || v.includes('اعادة'): return <Tag color="orange">إعادة كشف</Tag>;
        case v === 'أخرى':      return <Tag color="green">أخرى</Tag>;
        default:                return <Tag color="blue">{v || 'كشف'}</Tag>;
      }
    };

    // ── Patient search ──────────────────────────────────────────────────────
    const handleSearch = (value: string) => {
      setSearchTerm(value);
      setSelectedPatient(null);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (!value.trim()) { setSearchResults([]); return; }

      searchTimerRef.current = setTimeout(async () => {
        setSearchLoading(true);
        try {
          if (!navigator.onLine && cacheOwner()) {
            setSearchResults((await cachedPatients(JSON.stringify([selectedClinicId, clinicScopeIds]), value)).slice(0, 10));
            return;
          }
          const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, {
            params: { search: value.trim(), doctor_id: doctorId, limit: 10 },
            timeout: 10000,
          });
          const list: PatientResult[] = (res.data?.patients || res.data?.data || []).slice(0, 10);
          setSearchResults(list);
        } catch {
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 400);
    };

    const checkNewPatientPhone = async (): Promise<PatientResult[]> => {
      const normalized = normalizePatientPhoneInput(newPatientPhone);
      setNewPatientPhone(normalized);
      setPhoneMatches([]);
      setConfirmedSharedPhone(false);
      if (validateNewPatientEntry({
        name: newPatientName || 'Patient', phone: normalized, age: newPatientAge,
        ageUnit: newPatientAgeUnit, nationality: newPatientNationality, address: newPatientAddress,
      }).errors.phone) return [];

      setCheckingPhone(true);
      try {
        let candidates: PatientResult[] = [];
        const scope = JSON.stringify([selectedClinicId, clinicScopeIds]);
        if (!navigator.onLine && cacheOwner()) {
          candidates = (await cachedPatients(scope, normalized)).slice(0, 10);
        } else {
          const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, {
            params: { search: normalized, doctor_id: doctorId, limit: 10 }, timeout: 10000,
          });
          candidates = (res.data?.patients || res.data?.data || []).slice(0, 10);
        }
        const exact = candidates.filter(candidate =>
          phoneDigits(candidate.patient_phone || '') === phoneDigits(normalized)
        );
        setPhoneMatches(exact);
        return exact;
      } catch {
        // Duplicate checking improves safety but must not prevent offline entry.
        setPhoneMatches([]);
        return [];
      } finally {
        setCheckingPhone(false);
      }
    };

    const chooseExistingPatient = (patient: PatientResult) => {
      setNewPatientMode(false);
      setSelectedPatient(patient);
      setSearchTerm(patient.patient_name);
      setSearchResults([]);
      setPhoneMatches([]);
      setNewPatientErrors({});
    };

    const resetAddModal = () => {
      waitingOperationRef.current = '';
      setSearchTerm('');
      setSearchResults([]);
      setSelectedPatient(null);
      setVisitType('كشف');
      setNewPatientMode(false);
      setNewPatientName('');
      setNewPatientPhone('');
      setNewPatientAge('');
      setNewPatientAgeUnit('سنة');
      setNewPatientNationality('');
      setNewPatientAddress('');
      setNewPatientErrors({});
      setPhoneMatches([]);
      setCheckingPhone(false);
      setConfirmedSharedPhone(false);
    };

    // ── Add patient to Firestore waiting list ────────────────────────────────
    const handleAddToWaitingList = async () => {
      if (!selectedClinicId || selectedClinicId === '__all_clinics__' || !doctorId) {
        message.warning('Please select a specific clinic to add a patient to the waiting list');
        return;
      }

      let patientName = newPatientMode ? newPatientName.trim() : selectedPatient?.patient_name;
      let patientPhone = newPatientMode ? newPatientPhone.trim() : (selectedPatient?.patient_phone || '');
      let patientAge = '';
      let patientAddress = '';
      let patientNationality = '';
      if (newPatientMode) {
        const validation = validateNewPatientEntry({
          name: newPatientName, phone: newPatientPhone, age: newPatientAge,
          ageUnit: newPatientAgeUnit, nationality: newPatientNationality, address: newPatientAddress,
        });
        const exactPhoneMatches = phoneMatches.length > 0 ? phoneMatches : await checkNewPatientPhone();
        if (exactPhoneMatches.length > 0 && !confirmedSharedPhone) {
          validation.errors.phone = 'This phone already belongs to a patient. Select that patient or confirm it is shared.';
        }
        setNewPatientErrors(validation.errors);
        if (Object.keys(validation.errors).length > 0) return;
        patientName = validation.value.name;
        patientPhone = validation.value.phone;
        patientAge = validation.value.age ? `${validation.value.age} ${validation.value.ageUnit}` : '';
        patientAddress = validation.value.address;
        patientNationality = validation.value.nationality;
        setNewPatientName(patientName);
        setNewPatientPhone(patientPhone);
      }
      if (!patientName) {
        message.warning('Please select or enter a patient name');
        return;
      }

      setAddingPatient(true);
      try {
        if (canCaptureDoctor(doctorId) && cacheOwner()) {
          if (!waitingOperationRef.current) waitingOperationRef.current = crypto.randomUUID();
          const operationId = waitingOperationRef.current;
          const localId = `local_${operationId}`;
          const offlinePatientId = `offline_${operationId}`;
          const patientRecord: any = newPatientMode ? {
            id: localId, _id: localId, patient_id: offlinePatientId,
            patient_name: patientName, patient_phone: patientPhone, doctor_id: doctorId,
            doctor_name: doctorName || '', clinic_id: selectedClinicId, visit_type: visitType,
            date: toFirestoreDateStr(selectedDate), time: dayjs().format('HH:mm'), status: 'WAITING',
            age: patientAge, address: patientAddress, nationality: patientNationality,
            payment_type: 'cash', insurance_company_id: '', insurance_company_name: '',
            insurance_number: '', coverage_percentage: 0,
            visits: [], receipts: [], voice_notes: [],
            _offlineDraft: true, _offlineCreatedAt: Date.now(), offlinePending: true,
          } : selectedPatient;
          await queueWaitingPatient(selectedClinicId, toFirestoreDateStr(selectedDate), {
            ...patientRecord,
            patient_id: newPatientMode ? offlinePatientId : (selectedPatient!.patient_id || selectedPatient!._id),
            patient_name: patientName, patient_phone: patientPhone,
            doctor_id: doctorId, visit_type: visitType, time: dayjs().format('HH:mm'),
          }, operationId, { createPatient: newPatientMode, localPatientId: newPatientMode ? localId : '',
            scope: JSON.stringify([selectedClinicId, clinicScopeIds]),
            waitingDateKey: assistantWaitingListDateKey(selectedDate) });
          if (newPatientMode) {
            message.warning('Patient added to the waiting list on this device. Synchronization is pending.');
          } else message.warning('Saved on this device — waiting-list entry and queue number pending synchronization.');
          setAddModalOpen(false);
          resetAddModal();
          return;
        }
        const db = getFirestore();
        const currentDate = assistantWaitingListDateKey(selectedDate);
        const waitingListRef = collection(db, 'clinics', selectedClinicId, 'waiting_list', currentDate, 'patients');

        // Queue numbers are per doctor. Counting the whole clinic made a newly
        // added patient inherit large numbers from other doctors' queues.
        const doctorSnapshot = await getDocs(
          query(waitingListRef, where('doctor_id', '==', doctorId))
        );
        const nextPos = getNextDoctorQueuePosition(
          doctorSnapshot.docs.map((item) => item.data()),
          doctorId
        );

        const now = dayjs();
        await addDoc(waitingListRef, {
          patient_id:    newPatientMode ? `new_${Date.now()}` : selectedPatient!._id,
          patient_name:  patientName,
          patient_phone: patientPhone,
          age:             patientAge,
          address:         patientAddress,
          nationality:     patientNationality,
          payment_type:    newPatientMode ? 'cash' : (selectedPatient?.payment_type || 'cash'),
          insurance_company_id: newPatientMode ? '' : (selectedPatient?.insurance_company_id || ''),
          insurance_company_name: newPatientMode ? '' : (selectedPatient?.insurance_company_name || ''),
          insurance_number: newPatientMode ? '' : (selectedPatient?.insurance_number || ''),
          coverage_percentage: newPatientMode ? 0 : (selectedPatient?.coverage_percentage || 0),
          doctor_id:     doctorId,
          status:        'WAITING',
          visit_type:    visitType,
          arrivalTime:   serverTimestamp(),
          date:          currentDate,
          time:          now.format('HH:mm'),
          position:      nextPos,
          user_order_in_queue: nextPos,
          added_by:      'doctor',
        });

        message.success(`${patientName} added to waiting list`);
        setAddModalOpen(false);
        resetAddModal();
      } catch (err) {
        console.error('Error adding to waiting list:', err);
        message.error('Failed to add patient to waiting list');
      } finally {
        setAddingPatient(false);
      }
    };

    // ── Enter to doctor (same as assistant "دخول الي الطبيب"): FINISHED + POST /api/patients ──
    const normalizeQueueDate = (raw: string) =>
      raw.replace(/^(\d{4})-(\d{1,2})-(\d{1,2})$/, (_, y: string, m: string, d: string) =>
        `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      );

    const enterPatientFromDevice = async (patient: WaitingPatient, alreadyMarked?: any) => {
      const waitingClinicId = patient.waiting_clinic_id || patient.clinic_id ||
        (selectedClinicId !== '__all_clinics__' ? selectedClinicId : '');
      if (!waitingClinicId) {
        message.error('The clinic for this waiting patient could not be identified. Refresh once and try again.');
        return false;
      }
      const scope = JSON.stringify([selectedClinicId, clinicScopeIds]);
      let localPatient: any = alreadyMarked || await markWaitingPatientEntered(patient);
      if (!localPatient) {
        const saved = await cachedPatients(scope);
        localPatient = saved.find((item: any) => item._id === patient.patient_id || item.patient_id === patient.patient_id);
        const isUnsavedPatient = !patient.patient_id || /^(new_|offline_)/.test(String(patient.patient_id));
        const needsPatientCreation = isUnsavedPatient || localPatient?._offlineDraft === true ||
          String(localPatient?._id || '').startsWith('local_');
        const operationId = crypto.randomUUID();
        if (!localPatient && needsPatientCreation) {
          const localId = `local_${operationId}`;
          localPatient = {
            id: localId, _id: localId, patient_id: `offline_${operationId}`,
            patient_name: patient.patient_name, patient_phone: patient.patient_phone || '', doctor_id: doctorId,
            doctor_name: doctorName || '', clinic_id: waitingClinicId, visit_type: patient.visit_type || 'كشف',
            date: patient.date || toFirestoreDateStr(selectedDate), time: dayjs().format('HH:mm'), status: 'FINISHED',
            age: patient.age || '', address: patient.address || '', nationality: patient.nationality || '',
            visits: [], receipts: [], voice_notes: [],
            _offlineDraft: true, _offlineCreatedAt: Date.now(), offlinePending: true,
          };
          await queueWaitingPatient(waitingClinicId, patient.date || toFirestoreDateStr(selectedDate), localPatient,
            operationId, { createPatient: true, localPatientId: localId, existingWaitingDocId: patient.id,
              scope, entered: true, waitingDateKey: patient.waiting_date_key || assistantWaitingListDateKey(selectedDate) });
        } else if (localPatient) {
          localPatient = { ...localPatient, status: 'FINISHED', offlinePending: true };
          await queueWaitingPatient(waitingClinicId, patient.date || toFirestoreDateStr(selectedDate), localPatient,
            operationId, { createPatient: needsPatientCreation, localPatientId: needsPatientCreation ? localPatient._id : '',
              existingWaitingDocId: patient.id, scope, entered: true,
              waitingDateKey: patient.waiting_date_key || assistantWaitingListDateKey(selectedDate) });
        } else {
          message.warning('Download this existing patient while online before entering them offline.');
          return false;
        }
      }
      localPatient = { ...localPatient, status: 'FINISHED', offlinePending: true };
      upsertPatientLocally(localPatient);
      setWaitingPatients(current => current.filter(item => item.id !== patient.id));
      message.warning(`${patient.patient_name} entered and added to the patient list. Synchronization is pending.`);
      setCheckInTarget(null);
      return true;
    };

    const handleCheckIn = async (patient: WaitingPatient) => {
      const waitingClinicId = patient.waiting_clinic_id || patient.clinic_id ||
        (selectedClinicId !== '__all_clinics__' ? selectedClinicId : '');
      if (!waitingClinicId || !doctorId) return;
      setCheckInLoading(true);
      try {
        // A locally queued row can remain visible while the browser has already
        // switched back online. Mark that durable action as entered before
        // attempting a server document lookup; synchronization will create or
        // update the Firestore row using the same operation ID.
        const locallyQueuedPatient = cacheOwner() ? await markWaitingPatientEntered(patient) : null;
        if (locallyQueuedPatient || (!navigator.onLine && cacheOwner())) {
          await enterPatientFromDevice(patient, locallyQueuedPatient);
          return;
        }
        const db = getFirestore();
        const currentDate = patient.waiting_date_key || patient.date || toFirestoreDateStr(selectedDate);
        const patientDocRef = doc(db, 'clinics', waitingClinicId, 'waiting_list', currentDate, 'patients', patient.id);
        const snap = await getDoc(patientDocRef);
        if (!snap.exists()) {
          message.error('Patient not found on waiting list');
          return;
        }
        const p: any = snap.data();

        if (p.offline_patient_created && p.patient_mongo_id) {
          await updateDoc(patientDocRef, { status: 'FINISHED' });
          clearPatientListCache();
          await fetchPatients({ forceRefresh: true }).catch(() => ({ patients: [] }));
          message.success(`${patient.patient_name} entered — patient file was already synchronized`);
          setCheckInTarget(null);
          return;
        }

        const now = new Date();
        const finishTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const rawDate = p.date ?? currentDate;
        const patientClinicId = p.clinic_id || waitingClinicId;

        const patientPayload = {
          patient_name: p.patient_name ?? patient.patient_name,
          patient_phone: p.patient_phone ?? patient.patient_phone ?? '',
          patient_id: p.patient_id || patient.patient_id || patient.id,
          doctor_id: p.doctor_id || doctorId,
          doctor_name: p.doctor_name ?? doctorName ?? '',
          clinic_id: patientClinicId,
          date: normalizeQueueDate(String(rawDate)),
          time: finishTime,
          status: 'FINISHED',
          visit_type: p.visit_type ?? patient.visit_type ?? 'كشف',
          visit_speed: p.visit_speed ?? 'عادي',
          age: p.age ?? '',
          address: p.address ?? '',
          nationality: p.nationality ?? '',
          user_order_in_queue: p.user_order_in_queue ?? p.position ?? patient.position ?? 0,
          assistant_id: '',
          ...(p.file_number ? { file_number: p.file_number } : {}),
          insurance_company_id: p.insurance_company_id ?? '',
          insurance_company_name: p.insurance_company_name ?? '',
          insurance_number: p.insurance_number ?? '',
          coverage_percentage: p.coverage_percentage ?? 0,
          payment_type: p.payment_type ?? 'cash',
          consultation_fee: p.consultationPayment?.full_amount ?? p.consultationPayment?.amount ?? 0,
        };

        const postOnce = () =>
          axios.post(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, patientPayload, { timeout: 45000 });

        let response;
        try {
          response = await postOnce();
        } catch (e1) {
          try {
            response = await postOnce();
          } catch (e2) {
            console.error('POST /api/patients failed after retry:', e2);
            if (!(e2 as any)?.response && cacheOwner()) {
              await enterPatientFromDevice({ ...patient, ...p });
              return;
            }
            message.warning(
              `${patient.patient_name}: marked as entered, but saving to patient list failed. Refresh the patient list or try again.`
            );
            setCheckInTarget(null);
            return;
          }
        }

        // Mark as finished only after patient save succeeds to avoid race conditions
        // where waiting-list listeners refresh the patient list before POST completes.
        // This same write is observed by Dashboard's Firestore listener, which
        // force-refreshes the patient list as soon as it sees status='FINISHED' —
        // whether the Enter came from here or from the assistant portal.
        await updateDoc(patientDocRef, { status: 'FINISHED' });

        try {
          const fileNum = response?.data?.patient?.file_number;
          if (fileNum) await updateDoc(patientDocRef, { file_number: fileNum });
        } catch {
          // non-critical
        }

        // Drop the FE response cache and trigger a background refresh so
        // any fields not present in the POST response (e.g.,
        // externalServiceRequestCount aggregated separately by the list
        // endpoint) come into view shortly after.
        clearPatientListCache();
        fetchPatients({ forceRefresh: true }).catch((err) => {
          console.error('[handleCheckIn] refresh patient list failed:', err);
        });

        message.success(`${patient.patient_name} entered — added to patient list`);
        setCheckInTarget(null);
      } catch (err) {
        console.error('Error checking in patient:', err);
        message.error('Failed to update waiting list');
      } finally {
        setCheckInLoading(false);
      }
    };

    // ── Fetch all patient data by a resolved patient_id ─────────────────────
    // file_number is required to disambiguate when multiple patients share the same
    // phone number (the assistant portal does the same).
    const fetchPatientData = async (
      resolvedId: string,
      phone: string,
      fileNumber?: string,
    ) => {
      setQuickViewLoading(true);
      try {
        // Clinic-shared mode: when a clinic scope is known, send it on every clinical
        // call so any doctor in the clinic sees the same patient's visits / packages /
        // reports (independent of which doctor originally created them).
        const scope = clinicScopeIds && clinicScopeIds.length > 0 ? clinicScopeIds : null;
        const packageClinicId = scope ? scope[0] : (selectedClinicId || '');

        const visitsParams: any = { patient_id: resolvedId, doctor_id: doctorId, page: 1, limit: 9999 };
        if (fileNumber) visitsParams.file_number = fileNumber;
        if (phone) visitsParams.patient_phone = phone;
        if (scope) {
          visitsParams.use_clinic_scope = 'true';
          visitsParams.clinic_ids = scope.join(',');
        }

        const reportsParams: any = { patient_id: resolvedId, doctor_id: doctorId };
        if (phone) reportsParams.patient_phone = phone;
        if (fileNumber) reportsParams.file_number = fileNumber;
        if (scope) {
          reportsParams.use_clinic_scope = 'true';
          reportsParams.clinic_ids = scope.join(',');
        }

        const packagesParams: any = { doctor_id: doctorId };
        if (packageClinicId) packagesParams.clinic_id = packageClinicId;
        if (scope && scope.length > 1) packagesParams.clinic_ids = scope.join(',');
        // Identity hints so BE can resolve the patient across all doctors in the clinic
        // (the caller's patient_id may be a Firestore waiting-list id that no Mongo
        // PatientPackage row carries — phone/file_number bridge the gap).
        if (phone) packagesParams.patient_phone = phone;
        if (fileNumber) packagesParams.file_number = fileNumber;

        const [visitsRes, medHistRes, reportsRes, packagesRes, activePackagesRes] = await Promise.allSettled([
          axios.get(`${API.BASE_URL}/api/patients/visits`, {
            params: visitsParams,
            timeout: 20000,
          }),
          axios.get(`${API.BASE_URL}/api/medical-history/patient`, {
            params: { patient_id: resolvedId, doctor_id: doctorId },
            timeout: 15000,
          }),
          axios.get(`${API.BASE_URL}/api/patients/reports`, {
            params: reportsParams,
            timeout: 15000,
          }),
          axios.get(`${API.BASE_URL}/api/patient-packages/patient/${resolvedId}`, {
            params: packagesParams,
            timeout: 15000,
          }),
          axios.get(`${API.BASE_URL}/api/patient-packages/patient/${resolvedId}/active`, {
            params: packagesParams,
            timeout: 15000,
          }),
        ]);

        // Helper: extract an array from any of the common API response shapes
        // - direct array
        // - { data: [...] }   ← reports, packages, active-packages endpoints
        // - { visits: [...] } ← visits endpoint
        // - { reports: [...] } / { packages: [...] } ← legacy fallback
        const extractArray = (resp: any, ...keys: string[]): any[] => {
          if (Array.isArray(resp)) return resp;
          if (!resp || typeof resp !== 'object') return [];
          for (const k of keys) {
            if (Array.isArray(resp[k])) return resp[k];
          }
          if (Array.isArray(resp.data)) return resp.data;
          return [];
        };

        if (visitsRes.status === 'fulfilled') {
          setQuickViewVisits(extractArray(visitsRes.value.data, 'visits'));
        }
        if (medHistRes.status === 'fulfilled') {
          // medical-history returns { success, data: {...} } — keep the inner object
          const resp = medHistRes.value.data;
          setQuickViewMedHistory(resp?.data ?? resp);
        }
        if (reportsRes.status === 'fulfilled') {
          setQuickViewReports(extractArray(reportsRes.value.data, 'reports'));
        }
        if (packagesRes.status === 'fulfilled') {
          setQuickViewPackages(extractArray(packagesRes.value.data, 'packages'));
        }
        if (activePackagesRes.status === 'fulfilled') {
          setQuickViewActivePackages(extractArray(activePackagesRes.value.data, 'packages'));
        }
      } catch (err) {
        console.error('Quick view fetch error:', err);
      } finally {
        setQuickViewLoading(false);
      }
    };

    // Normalise a name for fuzzy comparison
    const normName = (s: any) =>
      String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

    // Score and pick the best candidate from a list. Higher score wins.
    //   +100 exact name match
    //   +50  partial name match (one contains the other)
    //   +30  has a file_number
    //   +20  is a "real" patient (not "Unknown Patient")
    const pickBestPatient = (list: any[], targetName?: string): any | null => {
      if (!Array.isArray(list) || list.length === 0) return null;
      const isReal = (p: any) =>
        p?.patient_name &&
        p.patient_name !== 'Unknown Patient' &&
        String(p.patient_name).trim() !== '';
      const target = normName(targetName);

      const scored = list.map((p) => {
        let score = 0;
        const pname = normName(p.patient_name);
        if (target && pname) {
          if (pname === target) score += 100;
          else if (pname.includes(target) || target.includes(pname)) score += 50;
        }
        if (p.file_number) score += 30;
        if (isReal(p)) score += 20;
        return { p, score };
      });

      scored.sort((a, b) => b.score - a.score);
      return scored[0].p;
    };

    // Search the database for the patient and return the best-matching record.
    // Runs three lookup strategies in parallel (by-phone, search by phone, search by name),
    // merges all candidates, and picks the one whose name best matches the waiting-list name.
    const findPatientInDb = async (
      patient: WaitingPatient,
    ): Promise<{ id?: string; file_number?: string }> => {
      const phone = patient.patient_phone?.trim();
      const name = patient.patient_name?.trim();
      const lookups: Promise<any>[] = [];

      if (phone) {
        lookups.push(
          axios.get(`${API.BASE_URL}/api/patients/by-phone`, {
            params: { phone, doctor_id: doctorId },
            timeout: 10000,
          }).then(r => r.data?.patients ?? []).catch(() => [])
        );
        lookups.push(
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, {
            params: { search: phone, doctor_id: doctorId, limit: 10 },
            timeout: 10000,
          }).then(r => r.data?.data ?? r.data?.patients ?? []).catch(() => [])
        );
      }
      if (name) {
        lookups.push(
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`, {
            params: { search: name, doctor_id: doctorId, limit: 10 },
            timeout: 10000,
          }).then(r => r.data?.data ?? r.data?.patients ?? []).catch(() => [])
        );
      }
      if (lookups.length === 0) return {};

      const results = await Promise.all(lookups);
      const seen = new Set<string>();
      const candidates: any[] = [];
      for (const list of results) {
        for (const p of (list || [])) {
          const id = p.patient_id || p._id;
          if (!id || String(id).startsWith('new_')) continue;
          const key = String(id);
          if (!seen.has(key)) {
            seen.add(key);
            candidates.push(p);
          }
        }
      }

      const best = pickBestPatient(candidates, name);
      if (!best) return {};
      return {
        id: best.patient_id || best._id,
        file_number: best.file_number ? String(best.file_number) : undefined,
      };
    };

    // ── Quick-view modal: open immediately then fetch all data ────────────────
    const openQuickViewModal = async (patient: WaitingPatient) => {
      // Open modal immediately so the user sees it right away
      setQuickViewVisits([]);
      setQuickViewMedHistory(null);
      setQuickViewReports([]);
      setQuickViewPackages([]);
      setQuickViewActivePackages([]);
      setQuickViewPatient(patient);
      setQuickViewLoading(true);

      // Always resolve the patient against the DB. The waiting-list patient_id can be:
      //   - a Firebase UID that no longer matches any MongoDB record (visits API returns 404)
      //   - one of multiple records sharing the same phone (need disambiguation by name)
      //   - "new_*" for manually-added patients
      // The DB search picks the best record (by name match + file_number) so we always
      // hit the right MongoDB document with the proper file_number for disambiguation.
      const { id: foundId, file_number: foundFile } = await findPatientInDb(patient);

      let resolvedId = foundId || patient.patient_id;
      const resolvedFileNumber = foundFile;

      if (!resolvedId || String(resolvedId).startsWith('new_')) {
        // Truly no match in the database — show basic info only
        setQuickViewLoading(false);
        return;
      }

      await fetchPatientData(resolvedId, patient.patient_phone || '', resolvedFileNumber);
    };

    // ── Handle eye button: always open the modal ─────────────────────────────
    const handleQuickView = (patient: WaitingPatient) => {
      if (!navigator.onLine && cacheOwner()) {
        void cachedPatients(JSON.stringify([selectedClinicId, clinicScopeIds])).then(async (patients: any[]) => {
          // Do not guess identity by phone: family members can share a number.
          const match = patients.find(p => p._id === patient.patient_id || p.patient_id === patient.patient_id);
          if (match) openPatientProfile(match);
          else if (String(patient.patient_id || '').startsWith('new_')) {
            const operationId = crypto.randomUUID();
            const localId = `local_${operationId}`;
            const draft: any = { id: localId, _id: localId, patient_id: `offline_${operationId}`,
              patient_name: patient.patient_name, patient_phone: patient.patient_phone || '', doctor_id: doctorId,
              doctor_name: doctorName || '', clinic_id: selectedClinicId, visit_type: patient.visit_type || 'كشف',
              date: patient.date || toFirestoreDateStr(selectedDate), time: patient.time || dayjs().format('HH:mm'), status: 'FINISHED',
              age: patient.age || '', address: patient.address || '', nationality: patient.nationality || '',
              visits: [], receipts: [], voice_notes: [],
              _offlineDraft: true, _offlineCreatedAt: Date.now(), offlinePending: true };
            await queueWaitingPatient(selectedClinicId!, patient.date || toFirestoreDateStr(selectedDate), {
              ...draft, doctor_id: doctorId,
            }, operationId, { createPatient: true, localPatientId: localId, existingWaitingDocId: patient.id,
              scope: JSON.stringify([selectedClinicId, clinicScopeIds]) });
            upsertPatientLocally(draft); openPatientProfile(draft);
            message.warning('Local patient file created. It will synchronize when the connection returns.');
          } else message.warning('This patient profile has not been downloaded on this device. Connect to prepare it first.');
        }).catch(() => message.error('Could not read the saved patient profile.'));
        return;
      }
      openQuickViewModal(patient);
    };

    if (!selectedClinicId) return null;

    return (
      <>
        <Card
          title={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Waiting List</span>
                <Space size={4}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => setAddModalOpen(true)}
                    title="Add patient to waiting list"
                  />
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchData(selectedDate)}
                    loading={loading}
                    size="small"
                  />
                </Space>
              </div>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                allowClear={false}
                size="small"
                style={{ width: '100%' }}
              />
            </div>
          }
          style={{ width: '100%', height: '100%', overflowY: 'auto', direction: 'rtl' }}
          bodyStyle={{ padding: '0' }}
          bordered
        >
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}><Spin /></div>
          ) : waitingPatients.length > 0 || localWaiting.length > 0 ? (
            <List
              dataSource={[...waitingPatients, ...localWaiting.filter(p => !waitingPatients.some(server => server.id === p.id))]}
              renderItem={(patient, index) => (
                <List.Item
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}>
                    <div style={{ flexShrink: 0 }}>
                      <Badge
                        count={patient.offlinePending ? '…' : getDisplayedQueuePosition(patient, index)}
                        style={{ backgroundColor: '#1890ff' }}
                      >
                        <UserOutlined style={{ fontSize: '18px', padding: '4px' }} />
                      </Badge>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {patient.patient_name}
                        {patient.offlinePending && <Tag color="orange">Pending sync</Tag>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {patient.time || formatTime(patient.arrivalTime)}
                        </Text>
                        {getStatusTag(patient.visit_type || '')}
                      </div>
                    </div>
                    {/* Open patient file modal */}
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined style={{ color: '#1890ff', fontSize: '16px' }} />}
                      onClick={() => handleQuickView(patient)}
                      title="عرض ملف المريض"
                      style={{ flexShrink: 0, padding: '0 4px' }}
                    />
                    {/* Check-in button */}
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />}
                      onClick={() => setCheckInTarget(patient)}
                      title="Enter this patient"
                      style={{ flexShrink: 0, padding: '0 4px' }}
                    />
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No patients in waiting list"
              style={{ padding: '20px' }}
            />
          )}
        </Card>

        {/* ── Quick-view Patient History Modal ────────────────────────────── */}
        <Modal
          open={!!quickViewPatient}
          onCancel={() => setQuickViewPatient(null)}
          footer={null}
          width={620}
          title={
            <Space>
              <EyeOutlined style={{ color: '#1890ff' }} />
              <span>ملف المريض — {quickViewPatient?.patient_name}</span>
            </Space>
          }
          styles={{ body: { maxHeight: '75vh', overflowY: 'auto', direction: 'rtl' } }}
        >
          {quickViewLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" tip="جاري تحميل ملف المريض..." />
            </div>
          ) : (
            <>
              {/* Basic info */}
              <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="الاسم">{quickViewPatient?.patient_name}</Descriptions.Item>
                <Descriptions.Item label="الهاتف">{quickViewPatient?.patient_phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="نوع الزيارة">{quickViewPatient?.visit_type || '—'}</Descriptions.Item>
                <Descriptions.Item label="وقت الوصول">
                  {quickViewPatient?.time || (quickViewPatient?.arrivalTime ? formatTime(quickViewPatient.arrivalTime) : '—')}
                </Descriptions.Item>
              </Descriptions>

              {/* Medical history */}
              {quickViewMedHistory && (
                quickViewMedHistory.chronic_diseases?.length > 0 ||
                quickViewMedHistory.allergies?.length > 0 ||
                quickViewMedHistory.current_medications?.length > 0 ||
                quickViewMedHistory.notes
              ) && (
                <>
                  <Divider orientation="right" style={{ fontSize: 13 }}>
                    <MedicineBoxOutlined style={{ marginLeft: 4 }} />
                    التاريخ الطبي
                  </Divider>
                  <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
                    {quickViewMedHistory.chronic_diseases?.length > 0 && (
                      <Descriptions.Item label="أمراض مزمنة">
                        {quickViewMedHistory.chronic_diseases.join('، ')}
                      </Descriptions.Item>
                    )}
                    {quickViewMedHistory.allergies?.length > 0 && (
                      <Descriptions.Item label="الحساسية">
                        {quickViewMedHistory.allergies.join('، ')}
                      </Descriptions.Item>
                    )}
                    {quickViewMedHistory.current_medications?.length > 0 && (
                      <Descriptions.Item label="أدوية حالية">
                        {quickViewMedHistory.current_medications.join('، ')}
                      </Descriptions.Item>
                    )}
                    {quickViewMedHistory.notes && (
                      <Descriptions.Item label="ملاحظات">{quickViewMedHistory.notes}</Descriptions.Item>
                    )}
                  </Descriptions>
                </>
              )}

            {/* Active packages — same layout as PatientDetail package balance card */}
            {quickViewActivePackages.length > 0 && (
              <>
                <Divider orientation="right" style={{ fontSize: 13 }}>
                  <GiftOutlined style={{ marginLeft: 4, color: '#52c41a' }} />
                  الباقات النشطة ({quickViewActivePackages.length})
                </Divider>
                <div style={{ marginBottom: 16, background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: '4px 0' }}>
                  {quickViewActivePackages.map((pkg: any, idx: number) => {
                    const remaining = Number(pkg.remaining_sessions ?? pkg.sessions_remaining ?? '');
                    const total     = Number(pkg.total_sessions ?? '');
                    const expiry    = pkg.expiry_date || pkg.expiryDate;
                    const statusColor = pkg.status === 'active' ? 'green' : pkg.status === 'finished' ? 'blue' : 'default';
                    const statusLabel = pkg.status === 'active' ? 'نشطة' : pkg.status === 'finished' ? 'مكتملة' : pkg.status === 'expired' ? 'منتهية' : (pkg.status || '');
                    return (
                      <div key={pkg.patient_package_id || idx} style={{ padding: '8px 12px', borderBottom: idx < quickViewActivePackages.length - 1 ? '1px solid #d6e4ff' : undefined }}>
                        <Text strong style={{ display: 'block', marginBottom: 2 }}>
                          {pkg.package_name || pkg.name || 'باقة'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          متبقي:{' '}
                          <strong style={{ color: remaining > 0 ? '#16a34a' : '#cf1322' }}>
                            {isNaN(remaining) ? '—' : remaining}
                          </strong>
                          {!isNaN(total) && total > 0 ? ` / ${total}` : ''}
                          {' · '}
                          الحالة:{' '}
                          <Tag color={statusColor} style={{ marginInlineStart: 2 }}>{statusLabel}</Tag>
                        </Text>
                        {expiry && (
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            تنتهي في: {new Date(expiry).toLocaleDateString('ar-EG')}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

              {/* All visits */}
              <Divider orientation="right" style={{ fontSize: 13 }}>
                <FileTextOutlined style={{ marginLeft: 4 }} />
                الزيارات ({quickViewVisits.length})
              </Divider>
              {quickViewVisits.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="لا توجد زيارات سابقة" style={{ marginBottom: 16 }} />
              ) : (
                <Collapse
                  size="small"
                  style={{ marginBottom: 16 }}
                  items={quickViewVisits.map((visit: any, idx: number) => {
                    // Backend stores prescriptions as `receipts[].drugs[].drug` (the
                    // actual medicine name). Older code paths used `medicines[].name`,
                    // so we accept both shapes for safety.
                    const drugLines: string[] = (visit.receipts || []).flatMap((r: any) => {
                      const items: Array<{ name: string; meta: string }> = [];
                      const drugs = Array.isArray(r?.drugs) ? r.drugs : [];
                      for (const d of drugs) {
                        const name = (d?.drug || d?.name || d?.medicine_name || '').toString().trim();
                        if (!name) continue;
                        const metaParts = [d?.doseQty, d?.frequency, d?.period, d?.timing, d?.route, d?.form]
                          .filter((v: any) => v != null && String(v).trim() !== '');
                        items.push({ name, meta: metaParts.join(' • ') });
                      }
                      const meds = Array.isArray(r?.medicines) ? r.medicines : [];
                      for (const m of meds) {
                        const name = (m?.medicine_name || m?.name || '').toString().trim();
                        if (name) items.push({ name, meta: '' });
                      }
                      return items.map((it) => (it.meta ? `${it.name} (${it.meta})` : it.name));
                    });
                    const hasReceiptDrugs = drugLines.length > 0;
                    const rawDate = visit.visit_date || visit.date || '';
                    const displayDate = rawDate
                      ? (() => {
                          const d = new Date(rawDate);
                          return isNaN(d.getTime()) ? String(rawDate) : d.toLocaleDateString('ar-EG');
                        })()
                      : '';

                    return {
                      key: idx,
                      label: (
                        <Space>
                          <Tag color="blue">{visit.visit_type || 'زيارة'}</Tag>
                          <span style={{ fontSize: 12, color: '#666' }}>{displayDate}</span>
                          {visit.time && (
                            <span style={{ fontSize: 11, color: '#999' }}>{visit.time}</span>
                          )}
                          {visit.doctor_name && (
                            <span style={{ fontSize: 11, color: '#999' }}>د. {visit.doctor_name}</span>
                          )}
                        </Space>
                      ),
                      children: (
                        <Descriptions size="small" column={1}>
                          {visit.complaint && (
                            <Descriptions.Item label="الشكوى">{visit.complaint}</Descriptions.Item>
                          )}
                          {visit.diagnosis && (
                            <Descriptions.Item label="التشخيص">{visit.diagnosis}</Descriptions.Item>
                          )}
                          {visit.notes && (
                            <Descriptions.Item label="ملاحظات">{visit.notes}</Descriptions.Item>
                          )}
                          {hasReceiptDrugs && (
                            <Descriptions.Item label="الوصفات">
                              {drugLines.map((line, ri) => (
                                <div key={ri} style={{ fontSize: 12, color: '#555' }}>{line}</div>
                              ))}
                            </Descriptions.Item>
                          )}
                          {!visit.complaint && !visit.diagnosis && !visit.notes && !hasReceiptDrugs && (
                            <Descriptions.Item>لا توجد تفاصيل</Descriptions.Item>
                          )}
                        </Descriptions>
                      ),
                    };
                  })}
                />
              )}

              {/* Reports / uploaded files */}
              {quickViewReports.length > 0 && (
                <>
                  <Divider orientation="right" style={{ fontSize: 13 }}>
                    <PictureOutlined style={{ marginLeft: 4, color: '#722ed1' }} />
                    التقارير والصور ({quickViewReports.length})
                  </Divider>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 8,
                    marginBottom: 16,
                  }}>
                    {quickViewReports.map((report: any, idx: number) => {
                      const url = report.image_url || report.file_url || report.url || report.imageUrl;
                      const name = report.description || report.report_name || report.name || report.title || report.report_type || `تقرير ${idx + 1}`;
                      const date = report.uploaded_at || report.date || report.created_at || report.uploadedAt;
                      return (
                        <div key={idx} style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: 6,
                          overflow: 'hidden',
                          textAlign: 'center',
                        }}>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={url}
                                alt={name}
                                style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </a>
                          ) : (
                            <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                              <PictureOutlined style={{ fontSize: 28, color: '#d9d9d9' }} />
                            </div>
                          )}
                          <div style={{ padding: '4px 6px' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                            {date && <div style={{ fontSize: 10, color: '#999' }}>{new Date(date).toLocaleDateString('ar-EG')}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

            {/* All packages history (excluding ones already shown as active) */}
            {(() => {
              const activeIds = new Set(
                quickViewActivePackages
                  .map((p: any) => p.patient_package_id)
                  .filter(Boolean)
              );
              const historyPackages = quickViewPackages.filter(
                (pkg: any) => !pkg.patient_package_id || !activeIds.has(pkg.patient_package_id)
              );
              if (historyPackages.length === 0) return null;

              const statusLabel = (s: string) =>
                s === 'active'   ? 'نشطة'
              : s === 'finished' ? 'مكتملة'
              : s === 'expired'  ? 'منتهية'
              :                    (s || 'غير محددة');
              const statusColor = (s: string) =>
                s === 'active'   ? 'green'
              : s === 'finished' ? 'blue'
              : s === 'expired'  ? 'red'
              :                    'default';

              return (
                <>
                  <Divider orientation="right" style={{ fontSize: 13 }}>
                    <GiftOutlined style={{ marginLeft: 4 }} />
                    سجل الباقات ({historyPackages.length})
                  </Divider>
                  <Collapse
                    size="small"
                    style={{ marginBottom: 16 }}
                    items={historyPackages.map((pkg: any, idx: number) => {
                      const remaining = Number(pkg.remaining_sessions ?? pkg.sessions_remaining ?? '');
                      const total     = Number(pkg.total_sessions ?? '');
                      const used      = (!isNaN(remaining) && !isNaN(total) && total > 0)
                        ? Math.max(0, total - remaining)
                        : undefined;
                      return {
                        key: pkg.patient_package_id || idx,
                        label: (
                          <Space>
                            <span style={{ fontWeight: 600 }}>{pkg.package_name || pkg.name || 'باقة'}</span>
                            <Tag color={statusColor(pkg.status)}>{statusLabel(pkg.status)}</Tag>
                            {!isNaN(remaining) && !isNaN(total) && total > 0 && (
                              <span style={{ fontSize: 11, color: '#888' }}>
                                متبقي: {remaining} / {total}
                              </span>
                            )}
                          </Space>
                        ),
                        children: (
                          <Descriptions size="small" column={2}>
                            {!isNaN(total) && total > 0 && (
                              <Descriptions.Item label="إجمالي الجلسات">{total}</Descriptions.Item>
                            )}
                            {used !== undefined && (
                              <Descriptions.Item label="مستخدم">{used}</Descriptions.Item>
                            )}
                            {!isNaN(remaining) && (
                              <Descriptions.Item label="متبقي">{remaining}</Descriptions.Item>
                            )}
                            {pkg.price !== undefined && (
                              <Descriptions.Item label="السعر">{pkg.price} ج</Descriptions.Item>
                            )}
                            {(pkg.start_date || pkg.startDate) && (
                              <Descriptions.Item label="تاريخ البدء">
                                {new Date(pkg.start_date || pkg.startDate).toLocaleDateString('ar-EG')}
                              </Descriptions.Item>
                            )}
                            {(pkg.expiry_date || pkg.expiryDate) && (
                              <Descriptions.Item label="تاريخ الانتهاء">
                                {new Date(pkg.expiry_date || pkg.expiryDate).toLocaleDateString('ar-EG')}
                              </Descriptions.Item>
                            )}
                          </Descriptions>
                        ),
                      };
                    })}
                  />
                </>
              );
            })()}
            </>
          )}
        </Modal>

        {/* ── Add Patient Modal ───────────────────────────────────────────── */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar style={{ background: '#e6f4ff', color: '#1677ff' }} icon={<UserAddOutlined />} />
              <div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>Add patient / إضافة مريض</div>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                  Search for an existing file or register a new patient
                </Text>
              </div>
            </div>
          }
          open={addModalOpen}
          onCancel={() => { setAddModalOpen(false); resetAddModal(); }}
          onOk={handleAddToWaitingList}
          okText={newPatientMode ? 'Register & add / تسجيل وإضافة' : 'Add selected patient / إضافة المريض'}
          cancelText="Cancel / إلغاء"
          confirmLoading={addingPatient}
          okButtonProps={{ disabled: newPatientMode ? false : !selectedPatient, size: 'large' }}
          cancelButtonProps={{ size: 'large' }}
          width={680}
          centered
          destroyOnClose
        >
          <div style={{ paddingTop: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
              <Button
                type={!newPatientMode ? 'primary' : 'default'}
                size="large"
                icon={<SearchOutlined />}
                onClick={() => { setNewPatientMode(false); setNewPatientErrors({}); }}
                style={{ height: 48 }}
              >
                Existing patient / مريض مسجل
              </Button>
              <Button
                type={newPatientMode ? 'primary' : 'default'}
                size="large"
                icon={<UserAddOutlined />}
                onClick={() => {
                  setNewPatientMode(true); setSelectedPatient(null); setSearchTerm(''); setSearchResults([]);
                }}
                style={{ height: 48 }}
              >
                New patient / مريض جديد
              </Button>
            </div>

            {!newPatientMode ? (
              <div style={{ minHeight: 220 }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Search patient / ابحث عن المريض</div>
                <Input
                  size="large"
                  prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder="Name, phone or file number / الاسم أو الهاتف أو رقم الملف"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                  autoFocus
                />
                {searchLoading && <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>}
                {!searchLoading && searchTerm.trim() && searchResults.length === 0 && !selectedPatient && (
                  <Alert style={{ marginTop: 12 }} type="info" showIcon
                    message="No matching patient found / لا يوجد مريض مطابق"
                    action={<Button size="small" onClick={() => setNewPatientMode(true)}>Register new</Button>} />
                )}
                {searchResults.length > 0 && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 230, overflowY: 'auto', marginTop: 10 }}>
                    {searchResults.map(p => (
                      <div key={p._id} onClick={() => chooseExistingPatient(p)}
                        style={{ padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar icon={<UserOutlined />} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{p.patient_name}</div>
                          <Space size={12} wrap>
                            {p.patient_phone && <Text type="secondary"><PhoneOutlined /> {p.patient_phone}</Text>}
                            {(p.file_number || p.file_id) && <Text type="secondary">File: {p.file_number || p.file_id}</Text>}
                            {p.age && <Text type="secondary">Age: {p.age}</Text>}
                          </Space>
                        </div>
                        <Button type="link">Select</Button>
                      </div>
                    ))}
                  </div>
                )}
                {selectedPatient && (
                  <Alert style={{ marginTop: 12 }} type="success" showIcon
                    message={<strong>{selectedPatient.patient_name}</strong>}
                    description={
                      <Space wrap>
                        {selectedPatient.patient_phone && <span>{selectedPatient.patient_phone}</span>}
                        {(selectedPatient.file_number || selectedPatient.file_id) &&
                          <span>File: {selectedPatient.file_number || selectedPatient.file_id}</span>}
                      </Space>
                    }
                    action={<Button size="small" onClick={() => { setSelectedPatient(null); setSearchTerm(''); }}>Change</Button>}
                  />
                )}
              </div>
            ) : (
              <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 12, padding: 16 }}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Patient name / اسم المريض <Text type="danger">*</Text></div>
                    <Input size="large" prefix={<UserOutlined />} placeholder="Full patient name"
                      value={newPatientName} status={newPatientErrors.name ? 'error' : undefined}
                      onChange={e => { setNewPatientName(e.target.value); setNewPatientErrors(v => ({ ...v, name: undefined })); }} />
                    {newPatientErrors.name && <Text type="danger" style={{ fontSize: 12 }}>{newPatientErrors.name}</Text>}
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Mobile number / رقم الهاتف <Text type="danger">*</Text></div>
                    <Input size="large" prefix={<PhoneOutlined />} placeholder="01012345678" inputMode="tel" dir="ltr"
                      value={newPatientPhone} status={newPatientErrors.phone ? 'error' : undefined}
                      suffix={checkingPhone ? <Spin size="small" /> : null}
                      onBlur={checkNewPatientPhone}
                      onChange={e => {
                        setNewPatientPhone(normalizePatientPhoneInput(e.target.value)); setPhoneMatches([]);
                        setConfirmedSharedPhone(false); setNewPatientErrors(v => ({ ...v, phone: undefined }));
                      }} />
                    {newPatientErrors.phone && <Text type="danger" style={{ fontSize: 12 }}>{newPatientErrors.phone}</Text>}
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Age / العمر <Text type="secondary" style={{ fontWeight: 400 }}>(optional)</Text></div>
                    <Space.Compact block>
                      <Input size="large" inputMode="numeric" placeholder="Age" value={newPatientAge}
                        status={newPatientErrors.age ? 'error' : undefined}
                        onChange={e => { setNewPatientAge(e.target.value.replace(/\D/g, '').slice(0, 5)); setNewPatientErrors(v => ({ ...v, age: undefined })); }} />
                      <Select size="large" value={newPatientAgeUnit} onChange={setNewPatientAgeUnit} style={{ width: 110 }}
                        options={[{ value: 'سنة', label: 'سنة' }, { value: 'شهر', label: 'شهر' }, { value: 'يوم', label: 'يوم' }]} />
                    </Space.Compact>
                    {newPatientErrors.age && <Text type="danger" style={{ fontSize: 12 }}>{newPatientErrors.age}</Text>}
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Nationality / الجنسية <Text type="secondary" style={{ fontWeight: 400 }}>(optional)</Text></div>
                    <Input size="large" placeholder="e.g. مصرية" maxLength={100} value={newPatientNationality}
                      status={newPatientErrors.nationality ? 'error' : undefined}
                      onChange={e => { setNewPatientNationality(e.target.value); setNewPatientErrors(v => ({ ...v, nationality: undefined })); }} />
                  </Col>
                  <Col span={24}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Address / العنوان <Text type="secondary" style={{ fontWeight: 400 }}>(optional)</Text></div>
                    <Input.TextArea rows={2} showCount maxLength={500} placeholder="Patient address" value={newPatientAddress}
                      status={newPatientErrors.address ? 'error' : undefined}
                      onChange={e => { setNewPatientAddress(e.target.value); setNewPatientErrors(v => ({ ...v, address: undefined })); }} />
                  </Col>
                </Row>

                {phoneMatches.length > 0 && (
                  <Alert style={{ marginTop: 14 }} type="warning" showIcon
                    message="A patient already uses this phone / يوجد مريض بنفس الرقم"
                    description={<div>
                      {phoneMatches.slice(0, 3).map(patient => (
                        <Button key={patient._id} type="link" style={{ paddingInline: 0, marginInlineEnd: 14 }}
                          onClick={() => chooseExistingPatient(patient)}>
                          Use {patient.patient_name}{patient.file_number ? ` · File ${patient.file_number}` : ''}
                        </Button>
                      ))}
                      <div>
                        <Checkbox checked={confirmedSharedPhone} onChange={e => {
                          setConfirmedSharedPhone(e.target.checked); setNewPatientErrors(v => ({ ...v, phone: undefined }));
                        }}>
                          This is a different patient sharing the same phone number
                        </Checkbox>
                      </div>
                    </div>} />
                )}
              </div>
            )}

            <Divider style={{ margin: '20px 0 14px' }} />
            <Row gutter={12} align="middle">
              <Col xs={24} md={12}>
                <div style={{ fontWeight: 600 }}>Visit type / نوع الزيارة <Text type="danger">*</Text></div>
                <Text type="secondary" style={{ fontSize: 12 }}>{selectedDate.format('DD/MM/YYYY')}</Text>
              </Col>
              <Col xs={24} md={12}>
                <Select value={visitType} onChange={setVisitType} size="large" style={{ width: '100%' }}>
                  {VISIT_TYPES.map(vt => <Option key={vt.value} value={vt.value}>{vt.label}</Option>)}
                </Select>
              </Col>
            </Row>
          </div>
        </Modal>

        {/* ── Check-In Confirm Modal ──────────────────────────────────────── */}
        <Modal
          title="Enter to doctor"
          open={!!checkInTarget}
          onCancel={() => !checkInLoading && setCheckInTarget(null)}
          onOk={() => checkInTarget && handleCheckIn(checkInTarget)}
          okText="Yes — enter & add to list"
          confirmLoading={checkInLoading}
          okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }, disabled: checkInLoading }}
          cancelText="Cancel"
          cancelButtonProps={{ disabled: checkInLoading }}
          width={380}
        >
          {checkInTarget && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff', marginBottom: 12 }} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>{checkInTarget.patient_name}</div>
              <div style={{ color: '#888', marginTop: 4 }}>
                {getStatusTag(checkInTarget.visit_type || '')}
              </div>
              <div style={{ marginTop: 16, color: '#555', lineHeight: 1.5 }}>
                Same as assistant: patient leaves the waiting list and is added to your patient list for today.
              </div>
            </div>
          )}
        </Modal>
      </>
    );
  }
);

export default SidebarWaitingList;
